import React from 'react';
import { Link } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';

type Section = { title: string; items: string[] };

const DOCS: Section[] = [
  { title: 'Getting started', items: [
    'Complete your profile with a photo, cover image and short bio so people can find you.',
    'Connect with friends, follow people and pages from Discover or the People to Follow panel to fill your home feed.',
    'Join groups and forums to meet people who share your interests and take part in discussions.',
    'Use Settings to manage privacy, notifications and blocked accounts.',
  ] },
  { title: 'Posting & media', items: [
    'Create posts, testimonies and Pulse videos from the Create button. You can edit or delete your posts from the post menu.',
    'Every image and video is checked by our safety system before it becomes public. Nude, partially nude or sexually explicit media is not allowed.',
    'If your media is held for review or rejected and you believe this is a mistake, open the post menu or Help and submit an appeal; a moderator will review it.',
  ] },
  { title: 'Pulse categories', items: [
    'Pulse videos can be published in Music, Motivation, Drama, Empowerment, Marriage, Entertainment, Kids Corner or Others.',
    'Use the category tabs on Pulse to browse a single category.',
  ] },
  { title: 'Messages, live & notifications', items: [
    'Send private messages from a profile or the Messages page; chats update in real time.',
    'Go live from the Go Live page. Viewers can watch and chat with you during the stream.',
    'Alerts shows likes, comments, follows, messages and live activity.',
  ] },
  { title: 'Wallet, ads & payments', items: [
    'Belivah uses PayPal for all payments. You can pay with your PayPal balance or an eligible debit/credit card.',
    'Your wallet is credited only after PayPal confirms the payment. Pending payments appear once PayPal completes them.',
    'Use your wallet balance to run ads from the Ads dashboard.',
  ] },
];

const PLAYBOOK: Section[] = [
  { title: '1. Build a clear identity', items: [
    'Use a recognisable profile photo and describe your ministry or calling in your bio.',
    'Pin your most important testimony or announcement near the top of your profile.',
  ] },
  { title: '2. Post consistently', items: [
    'Share a few times each week: devotionals, testimonies, event photos and short Pulse videos.',
    'Choose the right Pulse category (for example Marriage or Entertainment) so the right audience finds you.',
  ] },
  { title: '3. Engage your community', items: [
    'Reply to comments, thank people for sharing and follow back members of your community.',
    'Go live for prayer meetings, Bible studies or Q&A sessions and announce them in advance.',
  ] },
  { title: '4. Measure and grow', items: [
    'Check Analytics in Creator Studio to see which posts resonate most.',
    'Promote key posts or events with ads funded from your PayPal-backed wallet.',
  ] },
  { title: '5. Keep it safe and edifying', items: [
    'Follow our Terms and community guidelines. Modest, uplifting content performs best and avoids moderation holds.',
  ] },
];

const Guides: React.FC<{ variant?: 'docs' | 'playbook' }> = ({ variant = 'docs' }) => {
  const isPlaybook = variant === 'playbook';
  const sections = isPlaybook ? PLAYBOOK : DOCS;
  return (
    <div style={styles.container}>
      <TopNavigation />
      <main style={styles.content}>
        <header style={styles.header}>
          <h1 style={styles.title}>{isPlaybook ? 'Creator Playbook' : 'Guides & Docs'}</h1>
          <p style={styles.subtitle}>
            {isPlaybook ? 'Practical steps to grow a healthy, engaged community on Belivah.' : 'Everything you need to know about using Belivah.'}
          </p>
          <nav style={styles.tabs}>
            <Link to="/docs" style={{ ...styles.tab, ...(isPlaybook ? {} : styles.tabActive) }}>Guides</Link>
            <Link to="/playbook" style={{ ...styles.tab, ...(isPlaybook ? styles.tabActive : {}) }}>Playbook</Link>
            <Link to="/help" style={styles.tab}>Help</Link>
            <Link to="/creator-studio" style={styles.tab}>Creator Studio</Link>
          </nav>
        </header>
        {sections.map((s) => (
          <section key={s.title} style={styles.section}>
            <h2 style={styles.h2}>{s.title}</h2>
            <ul style={styles.ul}>
              {s.items.map((it) => <li key={it} style={styles.li}>{it}</li>)}
            </ul>
          </section>
        ))}
      </main>
    </div>
  );
};

const styles: { [k: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', background: 'var(--color-background-alt)' },
  content: { maxWidth: 900, margin: '0 auto', padding: '84px 16px 24px' },
  header: { background: '#0B6B6F', color: '#fff', padding: '20px 24px', borderRadius: 12 },
  title: { margin: 0, fontSize: 28, color: '#fff' },
  subtitle: { margin: '6px 0 0 0', fontSize: 14, color: 'rgba(255,255,255,0.9)' },
  tabs: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 14 },
  tab: { padding: '6px 14px', borderRadius: 999, background: 'rgba(255,255,255,0.15)', color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 600 },
  tabActive: { background: '#fff', color: '#0B6B6F' },
  section: { background: 'var(--color-surface)', borderRadius: 12, padding: 20, marginTop: 16, border: '1px solid var(--color-border)' },
  h2: { margin: '0 0 10px 0', color: 'var(--color-text-primary)', fontSize: 20 },
  ul: { margin: 0, paddingLeft: 20, color: 'var(--color-text-primary)' },
  li: { marginBottom: 8, lineHeight: 1.6 },
};

export default Guides;
